import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import Navbar from '../../components/Layouts/Navbar';
import Footer from '../../components/Layouts/Footer';
import PageSeo from '../../components/Common/PageSeo';
import { useTranslations } from '../../locales/useTranslations';
import { marked } from 'marked';
import { BiArrowBack, BiEnvelope, BiLinkExternal, BiMap, BiMoney, BiGroup, BiCalendar } from 'react-icons/bi';
import { fetchJobBySlug, fetchActiveJobs, getJobSlug } from '../../lib/jobsApi';

const JobDetailPage = ({ job }) => {
    const { t } = useTranslations();
    const router = useRouter();
    const { locale } = router;
    const baseUrl = locale === 'en' ? 'https://simplify-data.com' : 'https://simplify-data.de';

    // PageView tracking (client-seitig)
    useEffect(() => {
        if (job?.id && typeof window !== 'undefined') {
            const trackPageView = async () => {
                try {
                    const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'https://api.simplify-data.de';
                    await fetch(`${apiUrl}/api/pageview`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            collection: 'jobs',
                            item_id: String(job.id)
                        })
                    });
                } catch (error) {
                    console.debug('PageView tracking failed:', error);
                }
            };
            trackPageView();
        }
    }, [job?.id]);

    // Not Found State
    if (!job) {
        return (
            <>
                <Navbar />
                <div className="job-detail-page">
                    <div className="container">
                        <div className="job-detail-not-found">
                            <h1>{t('career_job_notFound_title')}</h1>
                            <p>{t('career_job_notFound_description')}</p>
                            <Link href="/karriere" className="job-apply-btn">
                                <BiArrowBack /> {t('career_job_backToCareer')}
                            </Link>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    const langCode = locale === 'de' ? 'de-DE' : 'en-US';
    const translation = job.translations?.find((t) => t.languages_code === langCode) || job.translations?.[0];
    const title = translation?.title || translation?.Titel || 'Job';
    const content = translation?.content || translation?.description || '';
    const htmlContent = content ? marked.parse(content) : '';
    const currentYear = new Date().getFullYear();
    const mailSubject = encodeURIComponent(title);
    const freePlätze = parseInt(job.anzahl) || 0;
    const canApply = freePlätze > 0;

    return (
        <>
            <PageSeo
                title={`${title} | ${t('career_page_title')}`}
                description={content.substring(0, 160).replace(/[#*_]/g, '')}
                canonical={`${baseUrl}/karriere/${getJobSlug(job, locale)}/`}
                openGraph={{
                    url: `${baseUrl}/karriere/${getJobSlug(job, locale)}/`,
                    title: `${title} | Simplify Data`,
                    description: content.substring(0, 160).replace(/[#*_]/g, ''),
                    images: [
                        {
                            url: `${baseUrl}/images/karriere/header.webp`,
                            width: 1200,
                            height: 630,
                            alt: title,
                        },
                    ],
                    siteName: 'Simplify Data',
                }}
                additionalLinkTags={[
                    {
                        rel: 'alternate',
                        hrefLang: 'de',
                        href: `https://simplify-data.de/karriere/${getJobSlug(job, 'de')}/`
                    },
                    {
                        rel: 'alternate',
                        hrefLang: 'en',
                        href: `https://simplify-data.com/karriere/${getJobSlug(job, 'en')}/`
                    }
                ]}
            />
            <Navbar />
            
            <div className="job-detail-page">
                <div className="job-detail-page__decoration job-detail-page__decoration--top-right" />
                <div className="job-detail-page__decoration job-detail-page__decoration--bottom-left" />

                <div className="container">
                    <Link href="/karriere" className="job-detail-page__back-link">
                        <BiArrowBack /> {t('career_job_backToCareer')}
                    </Link>

                    <div className="job-detail-page__header">
                        <span className="job-detail-page__badge">{t('career_jobs_title')}</span>
                        <h1 className="job-detail-page__title">{title}</h1>
                    </div>

                    <div className="row">
                        <div className="col-lg-8">
                            <div 
                                className="job-detail-page__content"
                                dangerouslySetInnerHTML={{ __html: htmlContent }}
                            />
                        </div>

                        <div className="col-lg-4">
                            <div className="job-detail-page__sidebar">
                                <div className="job-detail-page__info-card">
                                    <h3>{t('career_job_overview')}</h3>
                                    
                                    <div className="job-detail-page__info-item">
                                        <BiMap className="job-detail-page__info-icon" />
                                        <div>
                                            <strong>{t('career_jobs_location')}</strong>
                                            <span>{job.arbeitsort || 'Kiel'}</span>
                                        </div>
                                    </div>

                                    {job.lohn_std && (
                                        <div className="job-detail-page__info-item">
                                            <BiMoney className="job-detail-page__info-icon" />
                                            <div>
                                                <strong>{t('career_jobs_hourlyWage')}</strong>
                                                <span>{job.lohn_std}€</span>
                                            </div>
                                        </div>
                                    )}

                                    <div className={`job-detail-page__info-item ${!canApply ? 'job-detail-page__info-item--unavailable' : ''}`}>
                                        <BiGroup className="job-detail-page__info-icon" />
                                        <div>
                                            <strong>{t('career_jobs_availablePositions')}</strong>
                                            <span>{freePlätze}</span>
                                        </div>
                                    </div>

                                    <div className="job-detail-page__info-item">
                                        <BiCalendar className="job-detail-page__info-icon" />
                                        <div>
                                            <strong>{t('career_jobs_period')}</strong>
                                            <span>{currentYear}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="job-detail-page__apply-card">
                                    {canApply ? (
                                        <>
                                            <h3>{t('career_job_applyNow')}</h3>
                                            <p>{t('career_job_applyDescription')}</p>
                                            
                                            <Link
                                                href={`mailto:info@simplify-data.de?subject=${mailSubject}`}
                                                className="job-apply-btn"
                                            >
                                                <BiEnvelope /> {t('career_jobs_applyByEmail')}
                                            </Link>

                                            {job.arbeitsagentur_link && (
                                                <Link
                                                    href={job.arbeitsagentur_link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="job-apply-btn job-apply-btn--secondary"
                                                >
                                                    <BiLinkExternal /> {t('career_jobs_applyArbeitsagentur')}
                                                </Link>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <h3>{t('career_jobs_noPositionsAvailable')}</h3>
                                            <p>{t('career_no_jobs_description')}</p>
                                            
                                            <Link
                                                href="mailto:info@simplify-data.de?subject=Initiativbewerbung"
                                                className="job-apply-btn job-apply-btn--secondary"
                                            >
                                                <BiEnvelope /> {t('career_no_jobs_apply')}
                                            </Link>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </>
    );
};

export async function getStaticPaths({ locales }) {
    const paths = [];
    const activeJobs = await fetchActiveJobs();

    for (const locale of locales || ['de', 'en']) {
        for (const job of activeJobs) {
            const slug = getJobSlug(job, locale);
            const hasTranslation = job.translations?.some(
                (t) => t.languages_code === (locale === 'de' ? 'de-DE' : 'en-US')
            );
            if (slug && hasTranslation) {
                paths.push({ params: { slug }, locale });
            }
        }
    }

    return {
        paths,
        fallback: 'blocking',
    };
}

export async function getStaticProps({ params, locale }) {
    const slug = params?.slug;
    if (!slug) return { notFound: true };

    try {
        const job = await fetchJobBySlug(slug, locale || 'de');
        if (!job) return { notFound: true };

        const correctSlug = getJobSlug(job, locale || 'de');
        if (correctSlug && correctSlug !== slug) {
            return {
                redirect: {
                    destination: `/karriere/${correctSlug}/`,
                    permanent: true,
                },
            };
        }

        return {
            props: { job },
            revalidate: 300,
        };
    } catch {
        return { notFound: true };
    }
}

export default JobDetailPage;
